/* eslint-disable react/prop-types */
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Spin,
  Typography,
} from 'antd';
import { getDepartamentos } from 'api/configuracion/departamentos';
import {
  deleteEjecutor,
  getEjecutor,
  getEjecutores,
  patchEjecutor,
  postEjecutor,
} from 'api/pae/ejecutores';
import { getUnidadesEjecutoras } from 'api/pae/unidadesEjecutoras';
import { getZonasDeDiligencia } from 'api/pae/zonasDeDiligencia';
import { getUsuario, getUsuarios } from 'api/usuarios/usuarios';
import BusquedaAvanzadaUsers from 'components/BusquedaAvanzadaCajeros';
import FormSubmitControls from 'components/FormSubmitControls';
import { InputSearchPro } from 'components/InputSearch';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import Table from 'components/Table';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { isMutable } from 'utils/estadosGlobales';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import keyGenerator from 'utils/keyGenerator';
import { capitalize } from 'utils/normalizers';

export const permissionConfig = {
  permissionModel: 'ejecutorpae',
  catalogsModels: [
    'zonadediligenciapae',
    'unidadejecutorapae',
    'unidadresponsable',
    'usuario',
  ],
};

const Ejecutores = ({ permission }) => {
  const { estadosGlobales } = useSelector((state) => state.catalogs);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [mutable, setMutable] = useState(true);

  const [zonasDeDiligencia, setZonasDeDiligencia] = useState([]);
  const [unidadesEjecutoras, setUnidadesEjecutoras] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [usuario, setUsuario] = useState([]);
  const [visibleAdvancedUsuario, setVisibleAdvancedUsuario] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const res = await getEjecutores(null, false);
    setData(res);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;

    const fetchAll = async () => {
      setLoading(true);
      const [
        _zonasDeDiligencia,
        _unidadesEjecutoras,
        _departamentos,
        _data,
      ] = await Promise.all([
        getZonasDeDiligencia(),
        getUnidadesEjecutoras(),
        getDepartamentos(),
        getEjecutores(),
      ]);

      if (mounted) {
        setZonasDeDiligencia(_zonasDeDiligencia);
        setUnidadesEjecutoras(_unidadesEjecutoras);
        setDepartamentos(_departamentos);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setUsuario([]);
    form.resetFields();
  };

  const onClickAdd = () => {
    onCancel();
    keyGenerator(data, form, { keyProp: 'clave', filter: { prop: 'clave' } });
    setVisible(true);
  };

  const onClickEdit = async () => {
    const [key] = selectedRowKeys;
    const _ejecutor = await getEjecutor(key);
    let _usuario = null;
    if (_ejecutor.usuario) {
      const resUsuario = await getUsuario(_ejecutor.usuario);
      _usuario = resUsuario;
      setUsuario([_usuario]);
    }
    form.setFieldsValue({ ..._ejecutor, usuario: _usuario?.id });
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const onFinish = async (_continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchEjecutor(key, values, false)
        : postEjecutor(values));
      if (saved) {
        await fetchData();
        if (_continue) {
          if (!key) {
            setSelectedRowKeys([saved.id]);
          }
          await onClickEdit(saved.id);
        } else {
          onCancel();
        }
        return saved;
      }
      return saved;
    } catch (error) {
      onError(error, setLoading);
    } finally {
      setLoading(false);
    }
    return null;
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteEjecutor(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 120,
      fixed: 'left',
    },
    {
      titleText: 'Número de Empleado',
      dataIndex: 'numero_de_empleado',
      key: 'numero_de_empleado',
      width: 250,
    },
    {
      titleText: 'Nombre Completo',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      width: 250,
      ellipsis: true,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
      width: 180,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Número de Credencial',
      dataIndex: 'numero_de_credencial',
      key: 'numero_de_credencial',
      width: 250,
      render: (text) => text || '-',
    },
    {
      titleText: 'Fecha Expedición',
      dataIndex: 'fecha_expedicion_credencial',
      key: 'fecha_expedicion_credencial',
      width: 180,
      render: (text) => text?.format(DATE_FRONTEND_FORMAT) || '-',
    },
    {
      titleText: 'Fecha Vencimiento',
      dataIndex: 'fecha_vigencia_credencial',
      key: 'fecha_vigencia_credencial',
      width: 180,
      render: (text) => text?.format(DATE_FRONTEND_FORMAT) || '-',
    },
    {
      titleText: 'Credencial Expedida Por',
      dataIndex: 'credencial_expedida_por',
      key: 'credencial_expedida_por',
      width: 200,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Zona de Diligencia',
      dataIndex: 'zona_de_diligencia',
      key: 'zona_de_diligencia',
      width: 180,
      ellipsis: true,
      render: (text) => {
        const zona = zonasDeDiligencia.find((e) => e.id === text);
        return (
          <Typography.Text ellipsis={{ tooltip: zona?.descripcion }}>
            {zona?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
    {
      titleText: 'Unidad Responsable',
      dataIndex: 'unidad_responsable',
      key: 'unidad_responsable',
      width: 300,
      ellipsis: true,
      render: (text) => {
        const unidad = departamentos.find((e) => e.id === text);
        return (
          <Typography.Text ellipsis={{ tooltip: unidad?.descripcion }}>
            {unidad?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
    {
      titleText: 'Unidad Ejecutora',
      dataIndex: 'unidad_ejecutora',
      key: 'unidad_ejecutora',
      width: 300,
      ellipsis: true,
      render: (text) => {
        const unidad = unidadesEjecutoras.find((e) => e.id === text);
        return (
          <Typography.Text ellipsis={{ tooltip: unidad?.descripcion }}>
            {unidad?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 250,
      ellipsis: true,
      render: (text) => (
        <Typography.Text ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Typography.Text>
      ),
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 120,
      align: 'center',
      render: (value) => {
        const estado = estadosGlobales.find((eg) => eg.id === value);
        return (
          <Typography.Text>
            {estado?.descripcion || '-'}
          </Typography.Text>
        );
      },
    },
  ];

  const rules = {
    required: [{ required: true }],
  };

  const searchUsuario = async () => {
    try {
      setLoading(true);

      await form.validateFields(['usuario']);

      const { usuario: email } = form.getFieldsValue();
      const resUsuario = await getUsuarios({ email });

      if (!resUsuario.length) {
        message.info('No se encontraron coincidencias');
        setUsuario([]);
      }

      const [user] = resUsuario;

      form.setFieldsValue({ usuario: user.id });
      setUsuario([{
        ...user,
        nombre_completo: `${user?.first_name} ${user?.last_name || ''} ${user?.second_last_name || ''}`,
      }]);

      setLoading(false);
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const reset = () => {
    setUsuario([]);
    form.resetFields(['usuario']);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin
        tip="Cargando..."
        spinning={loading}
      >
        {!visible && (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            rowKey="id"
            permission={permission}
            hasAllViewPermission
            allowColumnSelector={false}
            deleteText="Cancelar"
          />
        )}
        {visible && (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Ejecutor`}
            extra={(
              <FormSubmitControls
                onFinish={(_, _continue) => onFinish(_continue)}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
                selectedRowKeys={selectedRowKeys}
                callback={(id) => {
                  const [key] = selectedRowKeys;
                  const normalized = data.map((e) => {
                    if (e.id === key) {
                      const match = estadosGlobales.find((eg) => eg.id === id);
                      form.setFieldsValue({ estados_globales: match.id });
                      setMutable(true);
                      return { ...e, estados_globales: id };
                    }
                    return e;
                  });
                  setData(normalized);
                }}
              />
            )}
            bordered={false}
          >

            <Form
              layout="vertical"
              form={form}
              onFinish={(_, _continue) => onFinish(_continue)}
              scrollToFirstError
              initialValues={{ estados_globales: 1, es_externo: false }}
            >

              <Row gutter={[10, 10]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="clave"
                    rules={rules.required}
                    label="Clave"
                  >
                    <Input disabled={!mutable || selectedRowKeys.length} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <NumericInput
                    name="numero_de_empleado"
                    label="Número de Empleado"
                    disabled={!mutable}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="nombre"
                    label="Nombre"
                    rules={rules.required}
                  >
                    <Input
                      disabled={!mutable}
                      maxLength={150}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="apellido_paterno"
                    label="Apellido Paterno"
                  >
                    <Input
                      disabled={!mutable}
                      maxLength={150}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="apellido_materno"
                    label="Apellido Materno"
                  >
                    <Input
                      disabled={!mutable}
                      maxLength={150}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="puesto"
                    label="Puesto"
                    normalize={capitalize}
                  >
                    <Input
                      disabled={!mutable}
                      maxLength={50}
                      allowClear
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="es_externo"
                    label="Es externo"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      trueFalse
                      allowClear
                      disabled={!mutable}
                    />
                  </Form.Item>
                </Col>
                <InputSearchPro
                  tooltip="Búsqueda por Correo del Usuario"
                  label="Usuario"
                  name="usuario"
                  breakPoints={{
                    xs: 24,
                    sm: 24,
                    md: 8,
                  }}
                  onClickAdvanced={() => setVisibleAdvancedUsuario(true)}
                  inputProps={{
                    onPressEnter: () => searchUsuario(),
                    onChange: ({ target: { value } }) => {
                      if (!value) {
                        reset();
                      }
                    },
                  }}
                  resultProps={{
                    breakPoints: {
                      xs: 24,
                      sm: 24,
                      md: 8,
                    },
                    name: 'usuario',
                    label: 'Usuario',
                    dataSource: usuario,
                    onClear: reset,
                    render: (record) => `${record?.first_name} ${record?.last_name || ''} ${record?.second_last_name || ''}`,
                  }}
                />
                <Col xs={24} md={8}>
                  <Form.Item
                    name="estados_globales"
                    label="Estado"
                    rules={rules.required}
                  >
                    <Select
                      dataSource={estadosGlobales}
                      disabled
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* Información de Credencial */}
              <Row gutter={[10, 10]}>

                <Col xs={24} md={8}>
                  <Form.Item
                    name="numero_de_credencial"
                    label="Número de Credencial"
                  >
                    <Input disabled={!mutable} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="fecha_expedicion_credencial"
                    label="Fecha de Expedición"
                  >
                    <DatePicker
                      disabled={!mutable}
                      format={DATE_FRONTEND_FORMAT}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item
                    name="fecha_vigencia_credencial"
                    label="Fecha de Vencimiento"
                  >
                    <DatePicker
                      disabled={!mutable}
                      format={DATE_FRONTEND_FORMAT}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="credencial_expedida_por"
                    label="Credencial Expedida Por"
                  >
                    <Input disabled={!mutable} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Información Organizacional */}
              <Row gutter={[10, 10]}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="zona_de_diligencia"
                    label="Zona de Diligencia"
                  >
                    <Select dataSource={zonasDeDiligencia} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="unidad_responsable"
                    label="Unidad Responsable"
                  >
                    <Select dataSource={departamentos} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="unidad_ejecutora"
                    label="Unidad Ejecutora"
                  >
                    <Select dataSource={unidadesEjecutoras} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Observaciones */}
              <Row gutter={[10, 10]}>
                <Col xs={24}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                  >
                    <Input.TextArea
                      disabled={!mutable}
                      allowClear
                      maxLength={500}
                      autoSize={{ minRows: 4, maxRows: 8 }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>

          </Card>
        )}

        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            onCancel();
          }}
          visible={visibleAlert}
          content={` el ejecutor ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
      <BusquedaAvanzadaUsers
        visible={visibleAdvancedUsuario}
        setVisible={setVisibleAdvancedUsuario}
        callback={(selected) => {
          const _selected = {
            ...selected,
            nombre_completo: `${selected?.first_name} ${selected?.last_name
              || ''} ${selected?.second_last_name || ''}`,
          };
          setUsuario([_selected]);
          form.setFieldsValue({ usuario: selected.id });
        }}
        dataExterno={[]}
        setDataExterno={() => { }}
      />
    </Row>
  );
};

export default Ejecutores;
